import axios from "axios";
import React, { useState, useEffect } from "react";
import { path } from "../../path";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

const AVATAR_COLORS = [
  "bg-blue-100 text-blue-800",
  "bg-teal-100 text-teal-800",
  "bg-amber-100 text-amber-800",
  "bg-rose-100 text-rose-800",
  "bg-violet-100 text-violet-800",
];

function fmt(amount) {
  return "₹" + amount;
}

function initials(name) {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

// ── Detail Cell ──────────────────────────────────────────────────────────────
function DetailCell({ icon, label, value }) {
  return (
    <div className="bg-gray-50 rounded-lg px-3 py-2 min-w-0">
      <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 flex items-center gap-1 mb-0.5">
        <span>{icon}</span> {label}
      </p>
      <p className="text-xs font-mono text-gray-800 truncate" title={value}>
        {value}
      </p>
    </div>
  );
}

// ── Payout Card ──────────────────────────────────────────────────────────────
function PayoutCard({ payout, index, onAccept, onReject }) {
  const [leaving, setLeaving] = useState(false);
  const avatarColor = AVATAR_COLORS[index % AVATAR_COLORS.length];

  const trigger = (action) => {
    setLeaving(true);
    setTimeout(() => {
      action === "accept"
        ? onAccept(payout.id, payout.userId)
        : onReject(payout.id, payout.userId);
    }, 260);
  };

  return (
    <div
      className={`bg-white border border-gray-100 rounded-xl overflow-hidden shadow-sm
        transition-all duration-[260ms] ease-in-out`}>
      <div className="p-4">
        {/* Top row: avatar + name/phone + amount */}
        <div className="flex items-center gap-3 mb-3">
          <div
            className={`w-10 h-10 rounded-full flex items-center justify-center
              text-sm font-semibold flex-shrink-0 ${avatarColor}`}>
            {initials(payout.name)}
          </div>

          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-900 leading-tight truncate">
              {payout.name}
            </p>
            <p className="text-xs font-mono text-gray-400">
              {payout.phoneNumer}
            </p>
          </div>

          <span
            className="text-sm font-mono font-semibold text-amber-800
            bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1 whitespace-nowrap">
            {fmt(payout.amount)}
          </span>
        </div>

        {/* Divider */}
        <div className="border-t border-gray-100 mb-3" />

        {/* Detail grid */}
        <div className="grid grid-cols-2 gap-2 mb-3">
          <DetailCell icon="✉" label="Email" value={payout.email} />
          <DetailCell icon="⬡" label="UPI ID" value={payout.paymentDetail} />
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <button
            onClick={() => trigger("reject")}
            disabled
            className="flex-1 flex items-center justify-center gap-1.5
              text-sm font-medium text-red-700
              bg-red-50 border border-red-200 rounded-lg py-2
              hover:bg-red-100 active:scale-[0.97]
              transition-all duration-100 cursor-pointer">
            <span className="text-xs font-bold">✕</span> Reject
          </button>

          <button
            onClick={() => trigger("accept")}
            disabled={leaving}
            className="flex-1 flex items-center justify-center gap-1.5
              text-sm font-medium text-emerald-700
              bg-emerald-50 border border-emerald-200 rounded-lg py-2
              hover:bg-emerald-100 active:scale-[0.97]
              transition-all duration-100 cursor-pointer">
            <span className="text-xs font-bold">✓</span> Accept
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Empty State ───────────────────────────────────────────────────────────────
function EmptyState() {
  return (
    <div className="text-center py-16 text-gray-400">
      <div className="text-4xl mb-3">🎉</div>
      <p className="text-base font-semibold text-gray-700 mb-1">
        All caught up!
      </p>
      <p className="text-sm">No pending payouts at the moment.</p>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function PendingPayouts() {
  const [payouts, setPayouts] = useState([]);

  const { token } = useSelector((state) => state.AuthReducer);

  const config = {
    headers: {
      "Access-Control-Allow-Origin": `${path}`,
      Authorization: `Bearer ${token}`,
    },
  };

  useEffect(() => {
    const fetchPayouts = async () => {
      try {
        const response = await axios.get(
          `${path}/marketing/pendingPayouts`,
          config
        );
        setPayouts(response.data);
      } catch (error) {
        console.error("Error in getting payouts: " + JSON.stringify(error));
      }
    };
    fetchPayouts();
  }, []);

  const handleAccept = async (id, userId) => {
    try {
      const response = await axios.post(
        `${path}/marketing/processPayout/${id}`,
        { userId },
        config
      );

      if (response.status === 200) {
        setPayouts((prev) => prev.filter((p) => p.id !== id));
        toast.success("Payout settled successfully!");
      } else {
        toast.error(response.data.error || "Something went wrong!");
      }
    } catch (error) {
      toast.error(error.response.data.error || "Something went wrong!");
      console.error("Error in getting payouts: " + JSON.stringify(error));
    }
  };

  // const handleReject = (id, userId) => {
  //   return;
  //   // setPayouts((prev) => prev.filter((p) => p.id !== id));
  // };

  const totalAmount = payouts.reduce((sum, p) => sum + p.amount, 0);

  return (
    <div className="bg-gray-50 pt-2 pb-8 px-4">
      <div className="max-w-xl mx-auto">
        {/* Header */}
        <div className="flex items-baseline justify-between mb-5">
          <h1 className="text-2xl font-bold tracking-tight text-gray-900">
            Pending Payouts
          </h1>
          <span
            className="text-xs font-medium text-gray-500 bg-white border
            border-gray-200 rounded-full px-3 py-1">
            {payouts.length} pending
          </span>
        </div>

        {/* Summary chips */}
        <div className="flex gap-2 mb-5 flex-wrap">
          <span className="text-xs bg-white border border-gray-200 rounded-md px-3 py-1.5 text-gray-600">
            Total:{" "}
            <span className="font-semibold text-gray-900 font-mono">
              {fmt(totalAmount)}
            </span>
          </span>
        </div>

        {/* Card list */}
        {payouts.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="flex flex-col gap-3">
            {payouts.map((payout, index) => (
              <PayoutCard
                key={payout.id}
                payout={payout}
                index={index}
                onAccept={handleAccept}
                onReject={() => {}}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
